import { z } from 'zod';
import { myBidRowSchema } from './bidder';
import { submissionViewSchema } from './ingestion';
import { paginatedSchema, paginationQuerySchema } from './pagination';
import { vehicleDetailSchema } from './vehicle';
import { depositSchema, depositStateSchema, paymentStateSchema } from './payment';
import {
  bidSchema,
  incrementBandsSchema,
  lotStateSchema,
  saleLotInputSchema,
  saleStyleSchema,
} from './sale';

/**
 * Admin-web (M6) DTOs — the endpoints the admin console needs that the earlier
 * milestones did not build (`docs/04-api-contract.md`, ADR 0019). Grouped here rather
 * than bloating `sale.ts`: the new-sale form's autosave draft, the pre-LIVE sale patch,
 * the unsold-pool re-add, and the read projections that feed the live console lists and
 * the client-side XLSX export (Details / Deposits / Bids). Money is integer kuruş;
 * timestamps are epoch-ms integers, matching the rest of the API.
 */

// --- PATCH /admin/sales/:id — edit a pre-LIVE sale --------------------------

/**
 * Partial update of a **pre-LIVE** sale (PUBLISHED, before `listingDate`). Deliberately
 * limited to **display fields with no schedule, DO-hot-state, scope, or conference
 * dependency** — a plain D1 write that can't desync the armed alarm/hot cache. NOT editable
 * here (cancel + recreate instead — ADR 0019): schedule (`style`/`startDate`/`listingDate`),
 * `incrementBands` (post-create barem editing deferred, M4.5), `jointConference`
 * (provisioning ran on create, M5), and **`targetCompany`** — a firm-scope change can't
 * reconcile an already-provisioned joint conference's registrants nor evict already-connected
 * out-of-scope realtime sockets (a PUBLISHED sale accepts connections before listing). At
 * least one field must be present.
 */
export const saleUpdateSchema = z
  .object({
    title: z.string().max(200).nullable().optional(),
    location: z.string().max(200).nullable().optional(),
    defaultReserve: z.number().int().positive().nullable().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, {
    message: 'at least one field must be provided',
  });
export type SaleUpdateInput = z.infer<typeof saleUpdateSchema>;

// --- POST /admin/sales/:id/lots/re-add — unsold-pool re-add -----------------

/**
 * Re-add an `UNSOLD` lot to its (LIVE/PAUSED) sale as a new pass at the tail — the
 * unsold-pool re-run from the live console. `reserve` optionally **lowers** the hidden
 * reserve; `saleMode` optionally switches to `guaranteed` (kesin satış). Both omitted →
 * inherit the source lot. The vehicle, starting price, increment, and pass are resolved
 * server-side from the source lot (ADR 0019).
 */
export const reAddLotSchema = z.object({
  lotId: z.uuid(), // the UNSOLD lot to re-run
  reserve: z.number().int().positive().nullable().optional(), // kuruş; lower the hidden reserve
  // One-way override: only a switch TO guaranteed is allowed (never back to reserve-gated
  // normal, which would make the re-run harder to sell). Omit to inherit the source lot.
  saleMode: z.literal('guaranteed').optional(),
  // A re-add is a **fresh per-auction pass**, so its government barcode is regenerated: the
  // operator supplies a new one here (there is no in-app barcode generator — it is an external
  // government identifier). Omit if the auction has none.
  govBarcode: z.string().max(120).optional(),
});
export type ReAddLotInput = z.infer<typeof reAddLotSchema>;

// --- GET/PUT /admin/sales/draft — new-sale-form autosave --------------------

/**
 * A **lenient** autosave blob for the new-sale form (one per user, KV-backed) — a
 * work-in-progress, so every field is optional and half-filled lots are allowed. Kept
 * `loose` so a form field added later still round-trips; sizes are capped to bound the KV
 * value. This is *not* a create payload — the form validates against `saleCreateSchema`
 * only on submit.
 */
export const saleDraftSchema = z.looseObject({
  title: z.string().max(200).optional(),
  location: z.string().max(200).optional(),
  defaultReserve: z.number().int().optional(),
  incrementBands: incrementBandsSchema.optional(),
  listingDate: z.number().int().optional(),
  startDate: z.number().int().optional(),
  style: saleStyleSchema.optional(),
  jointConference: z.boolean().optional(),
  targetCompany: z.array(z.uuid()).max(100).optional(),
  // Half-filled lots during editing — each partial, so the draft never blocks on a
  // not-yet-complete row.
  lots: z.array(saleLotInputSchema.partial()).max(500).optional(),
});
export type SaleDraft = z.infer<typeof saleDraftSchema>;

/** `GET /admin/sales/draft` → the saved draft, or `null` when none exists. */
export const saleDraftResponseSchema = saleDraftSchema.nullable();
export type SaleDraftResponse = z.infer<typeof saleDraftResponseSchema>;

// --- GET /admin/vehicles/eligible — new-sale vehicle picker -----------------

/** A vehicle eligible to add to a sale (positive auction price, not on an active lot). */
export const eligibleVehicleSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  plate: z.string(),
  brand: z.string().nullable(),
  series: z.string().nullable(),
  // M11: enough to recognise the car in the picker without opening it.
  modelName: z.string().nullable(),
  year: z.number().int().nullable(),
  coverUrl: z.string().nullable(),
  auctionPrice: z.number().int().nullable(), // kuruş (starting-price default)
  auctionReserve: z.number().int().nullable(), // kuruş (hidden-reserve default)
  auctionIncrement: z.number().int(), // kuruş
  currency: z.string(),
});
export type EligibleVehicle = z.infer<typeof eligibleVehicleSchema>;

// --- GET /admin/sales/:id/deposits — live console + XLSX Deposits sheet -----

/** A deposit enriched with the bidder identity for the admin deposit list / export. */
export const depositAdminViewSchema = depositSchema.extend({
  bidderName: z.string().nullable(), // "First Last" (or null if unnamed)
  bidderEmail: z.string().nullable(),
  plateSnapshot: z.string().nullable(), // the lot's plate, for the sheet
});
export type DepositAdminView = z.infer<typeof depositAdminViewSchema>;

// --- GET /admin/sales/:id/bids — live console history + XLSX Bids sheet ------

/** A bid enriched with the bidder name + lot reference for the admin bid list / export. */
export const bidAdminViewSchema = bidSchema.extend({
  bidderName: z.string().nullable(),
  orderIndex: z.number().int(), // the lot's order in the sale
  plateSnapshot: z.string().nullable(),
  currency: z.string(), // the bid's lot currency (lots can differ, e.g. EUR) — format per-bid
});
export type BidAdminView = z.infer<typeof bidAdminViewSchema>;

// --- GET /admin/submissions — moderation queue (M4.6 route, typed for M6) ---

/**
 * The moderation-queue row: the client {@link submissionViewSchema} enriched with the
 * vehicle fields the admin list renders (`routes/submissions.ts`). Typed here so the admin
 * app binds to the shared contract instead of a local shape.
 */
export const adminSubmissionViewSchema = submissionViewSchema.extend({
  plate: z.string().nullable(),
  brand: z.string().nullable(),
  series: z.string().nullable(),
  startingPrice: z.number().int().nullable(), // kuruş (vehicle auction price)
  /** M11: how many photos/videos arrived with the submission (0 when none). */
  mediaCount: z.number().int().default(0),
  /** M13: the submitting firm's name — the queue's only clue to who sent the car. */
  companyName: z.string().nullable(),
});
export type AdminSubmissionView = z.infer<typeof adminSubmissionViewSchema>;

/**
 * `GET /admin/submissions/:id` (M11) — the queue row plus the **whole submitted vehicle**.
 * Since M11 a submission can carry a spec sheet, a description, an expertise report and
 * photos; three columns are not enough to judge one. The reserve IS included: the moderator
 * is a sale manager and it is part of what they are approving.
 */
export const adminSubmissionDetailSchema = adminSubmissionViewSchema
  .omit({ plate: true, brand: true, series: true, startingPrice: true, mediaCount: true })
  .extend({ vehicle: vehicleDetailSchema });
export type AdminSubmissionDetail = z.infer<typeof adminSubmissionDetailSchema>;

// --- Pagination (M10) --------------------------------------------------------

// Pagination idiom (`paginationQuerySchema` / `paginatedSchema`) lives in `./pagination` —
// hoisted out of this admin module once the bidder surface (`/me/bids`) adopted the envelope.

// --- POST /admin/deposits/:id/refund — M10 refund/void flow ------------------

/**
 * Result of a deposit refund (M10, ADR 0011). `op` reports which bank operation actually
 * succeeded — the server tries a same-day `void` first and falls back to a post-settlement
 * `credit` automatically (one admin action; the operator never picks). The updated deposit and
 * the payment's terminal state ride along so the client can render without a refetch.
 */
export const depositRefundResultSchema = z.object({
  deposit: depositSchema,
  op: z.enum(['void', 'credit']),
  paymentState: paymentStateSchema,
});
export type DepositRefundResult = z.infer<typeof depositRefundResultSchema>;

// --- POST /admin/deposits/:id/reconcile — ambiguous-refund reconciliation ----

/**
 * Result of reconciling an ambiguously-failed refund against the bank's order-status
 * inquiry (ADR 0011 amendment). `void`/`credit`: the return DID land at the bank — the
 * payment is finalized to the matching terminal state. `reverted`: the charge still
 * stands — the deposit is CASed back to `HELD` and the refund can simply be retried.
 */
export const depositReconcileResultSchema = z.object({
  op: z.enum(['void', 'credit', 'reverted']),
  depositState: depositStateSchema,
  paymentState: paymentStateSchema,
});
export type DepositReconcileResult = z.infer<typeof depositReconcileResultSchema>;

// --- POST /admin/payments/:id/reconcile — stuck-PRE3D reconciliation ---------

/**
 * Result of reconciling a payment stuck in `PRE3D` (a lost 3DS callback — ADR 0011
 * amendment; both kinds). Polarity is FLIPPED vs the refund reconcile: `confirmed` means
 * the bank charge stands and the purchase was delivered (payment `SUCCESSFUL` + role/deposit
 * effects); `voided`/`refunded` means the bank returned the money — the payment is terminal
 * and the user's blocked purchase/deposit slot is freed. `failed` is the mark-failed escape
 * hatch's result (`POST /admin/payments/:id/mark-failed` — human-verified never-charged).
 * `grantedRole` rides along for a confirmed membership (null for deposits).
 */
export const paymentReconcileResultSchema = z.object({
  op: z.enum(['confirmed', 'voided', 'refunded', 'failed']),
  paymentState: paymentStateSchema,
  grantedRole: z.string().nullable(),
});
export type PaymentReconcileResult = z.infer<typeof paymentReconcileResultSchema>;

// --- GET /admin/users/:id/payments — bidder profile: payment attempts --------

/** A user's payment attempt (deposit or membership) for the admin bidder profile — the
 * stuck-PRE3D reconcile/mark-failed surface (buttons enable on a stale `PRE3D` row). */
export const adminUserPaymentRowSchema = z.object({
  id: z.uuid(),
  orderId: z.string(),
  kind: z.enum(['deposit', 'GOLDBIDDER']),
  grantedRole: z.string().nullable(),
  saleId: z.uuid().nullable(),
  amount: z.number().int(),
  currency: z.string(),
  state: paymentStateSchema,
  createdAt: z.number().int(),
  // When the callback claimed WAITING→PRE3D — the stuck-staleness anchor the UI must age
  // from (createdAt fallback for pre-migration rows), mirroring the server predicate.
  pre3dAt: z.number().int().nullable(),
  finalizedAt: z.number().int().nullable(),
});
export type AdminUserPaymentRow = z.infer<typeof adminUserPaymentRowSchema>;

// --- GET /admin/users/:id/bids — bidder profile: cross-sale bid history ------

/** The bidder-profile bid row: the self-view row ({@link myBidRowSchema}) plus the lot's
 * lifecycle state so the admin sees outcome context (SOLD/UNSOLD/…) per pass. */
export const adminUserBidRowSchema = myBidRowSchema.extend({
  lotState: lotStateSchema,
});
export type AdminUserBidRow = z.infer<typeof adminUserBidRowSchema>;

// --- GET /admin/users/:id/deposits — bidder profile: deposits + refund surface

/** A user's deposit enriched with sale/lot/payment context — the refund flow's primary UI
 * surface (the Refund button enables on `state === 'HELD'`). */
export const adminUserDepositRowSchema = depositSchema.extend({
  saleCode: z.string(),
  plateSnapshot: z.string().nullable(),
  orderId: z.string(), // the bank order id (payments.order_id) — quoted in support/reconciliation
  paymentState: paymentStateSchema,
});
export type AdminUserDepositRow = z.infer<typeof adminUserDepositRowSchema>;

// --- GET /admin/audit — audit log viewer (M10) -------------------------------

/**
 * Audit-viewer filters + pagination. `entityType`/`action` are free strings (the log has no
 * CHECK on them — the UI offers known values best-effort); `from`/`to` bound `occurredAt`
 * (epoch-ms, inclusive). Every filter is optional; combinations AND together.
 */
export const auditLogQuerySchema = paginationQuerySchema.extend({
  entityType: z.string().max(60).optional(),
  entityId: z.string().max(120).optional(),
  // Free text like the other filters (NOT z.uuid()): the admin UI's filter input fires per
  // debounce tick, so a partial uuid mid-typing must yield an empty result, not a 400.
  actorId: z.string().max(64).optional(),
  /** M13: filter to one firm's ingestion writes (rows whose actor is an API key, not a user). */
  actorCompanyId: z.string().max(64).optional(),
  action: z.string().max(60).optional(),
  from: z.coerce.number().int().nonnegative().optional(),
  to: z.coerce.number().int().nonnegative().optional(),
});
export type AuditLogQuery = z.infer<typeof auditLogQuerySchema>;

/**
 * One audit row as served to the viewer. `beforeData`/`afterData` are the stored raw JSON
 * strings (client pretty-prints); they and `requestIp` are **null on old rows** — the KVKK
 * retention sweep (ADR 0022) anonymizes them past the retention horizon, so the viewer must
 * render a redacted marker, not assume presence.
 */
export const auditLogRowSchema = z.object({
  id: z.uuid(),
  occurredAt: z.number().int(),
  actorId: z.uuid().nullable(), // null = no user actor: a firm (see below) or system (alarm/cron)
  /**
   * M13: the firm behind an ingestion API key. `actorId` is null on those rows because
   * `audit_log.actor_id` FKs `users` — so without this field a firm's submission would be
   * indistinguishable from cron activity in the viewer.
   */
  actorCompanyId: z.uuid().nullable(),
  actorRole: z.string().nullable(),
  /**
   * The actor's display identity, resolved server-side from `users` at read time (the log itself
   * stores only the id). Both are null when there is no user actor (firm/system rows), when the
   * user no longer exists, or when they are soft-erased — every other admin read surface hides
   * erased users, and re-attaching a name to a right-to-be-forgotten account in the one history
   * view would undo that. The viewer falls back to `actorId`, which is what it showed before.
   *
   * Read-time resolution means a rename is reflected retroactively. That is the wanted behaviour
   * for a "who did this" column — `actorId` remains the stable handle, and the payload snapshots
   * hold whatever the name was at the time.
   */
  actorName: z.string().nullable(),
  actorEmail: z.string().nullable(),
  entityType: z.string(),
  entityId: z.string(),
  action: z.string(),
  beforeData: z.string().nullable(), // raw JSON text or null (never written / retention-nulled)
  afterData: z.string().nullable(),
  requestIp: z.string().nullable(),
  requestId: z.string().nullable(),
});
export type AuditLogRow = z.infer<typeof auditLogRowSchema>;
